import React, { useState } from 'react';
import { AlertCircle, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';

import EmptyState from '@/components/EmptyState';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export default function DataTable({
  columns,
  data,
  loading,
  emptyMessage = 'No data available',
  onRowClick,
  pagination,
}) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedData = [...(data || [])].sort((firstRow, secondRow) => {
    if (!sortConfig.key) return 0;

    const comparison = compareValues(firstRow?.[sortConfig.key], secondRow?.[sortConfig.key]);
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }

      return { key, direction: 'asc' };
    });
  };

  if (loading) {
    return <LoadingSkeleton type="table" />;
  }

  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={<AlertCircle className="h-8 w-8" />}
        title="Nothing to show"
        description={emptyMessage}
      />
    );
  }

  const page = pagination?.page || 1;
  const pageSize = pagination?.pageSize || data.length;
  const total = pagination?.total || data.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="overflow-hidden rounded-xl border border-slate-700 bg-slate-800">
      <Table>
        <TableHeader className="bg-slate-900">
          <TableRow className="border-slate-700 hover:bg-slate-900">
            {columns.map((column) => (
              <TableHead key={column.key} className="px-4 py-3 text-slate-400">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-left"
                  onClick={() => handleSort(column.key)}
                >
                  {column.label}
                  <ArrowUpDown className="h-3.5 w-3.5" />
                </button>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.map((row, rowIndex) => (
            <TableRow
              key={row.id || rowIndex}
              className={`border-slate-700 text-slate-300 hover:bg-slate-700/50 ${onRowClick ? 'cursor-pointer' : ''}`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((column) => (
                <TableCell key={`${row.id || rowIndex}-${column.key}`} className="px-4 py-3">
                  {column.render ? column.render(row[column.key], row) : row[column.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pagination ? (
        <div className="flex items-center justify-between border-t border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-400">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"
              disabled={page <= 1}
              onClick={() => pagination.onPageChange(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"
              disabled={page >= totalPages}
              onClick={() => pagination.onPageChange(page + 1)}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
